import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { api } from "./api";

/**
 * Der Wasserverbrauch aller Objekte.
 *
 * Stand bis zum 04.09.2026 auf der Uebersicht, der Startseite der
 * Geschaeftsleitung. Mit dem Wegfall jener Seite ist die Tabelle hierher
 * gewandert und haengt an der Objektliste: dort steht ohnehin, was ueber alle
 * Objekte hinweg gilt, und von dort kommt man mit einem Klick zum Objekt.
 */

interface Wasserzeile {
  objektId: string;
  objekt: string;
  kunde: string;
  kundeId: string;
  letzteAblesung: string;
  stand: number;
  proTag: number | null;
  tageImAbschnitt: number | null;
  monatProTag: number | null;
  vormonatProTag: number | null;
  letzteWoche: number | null;
  basis: number | null;
  auffaellig: boolean;
  leer: boolean | null;
  belegteTage: number | null;
  grundlast: number | null;
  vergleich: "leer" | "belegt" | "alle";
  leckverdacht: boolean;
}

/**
 * Der Wasserverbrauch aller Objekte auf einen Blick.
 *
 * Ein Leck meldet sich nicht. Es steht als Zahl in einem Bericht, den niemand
 * mit dem vom letzten Mal vergleicht, und faellt erst auf der Wasserrechnung
 * auf, drei Monate spaeter. Diese Tabelle macht den Vergleich einmal fuer alle
 * Objekte.
 *
 * Verglichen wird der Verbrauch **je Tag** und nicht je Besuch. Zwischen zwei
 * Ablesungen liegen mal drei, mal fuenf Tage; zwei Kubikmeter sind in drei
 * Tagen etwas anderes als in fuenf, sehen als Zahl aber gleich aus.
 *
 * Zwei Stufen. Rot mit Marke heisst: das Haus stand leer und hat trotzdem
 * deutlich mehr verbraucht als sonst, wenn es leer steht. Das ist der
 * Verdacht, dem man nachgeht. Bernstein heisst nur "mehr als sonst", und
 * solange Gaeste da waren, ist das zuerst eine Erklaerung und kein Schaden.
 *
 * Die Unterscheidung haengt an den Belegungen. Seit die Aufenthalte entfernt
 * sind, pflegt sie niemand mehr: jedes Objekt gilt damit als leer, und Rot
 * bedeutet weiterhin nur "mehr als sonst". Die Spalte Haus sagt, worauf die
 * Aussage beruht. Die Rechnung bleibt, weil die Belegungen aus einer Anlage
 * mit Bestand weiterhin gelesen werden.
 */
export function Wasseruebersicht() {
  const { t, i18n } = useTranslation();
  const [zeilen, setZeilen] = useState<Wasserzeile[] | null>(null);
  const [alle, setAlle] = useState(false);

  useEffect(() => {
    void api.get<{ objekte: Wasserzeile[] }>("/wasser")
      .then((a) => setZeilen(a.objekte))
      .catch(() => setZeilen([]));
  }, []);

  if (!zeilen || zeilen.length === 0) return null;

  const auffaellige = zeilen.filter((z) => z.auffaellig);
  const verdacht = zeilen.filter((z) => z.leckverdacht);
  const gezeigt = alle ? zeilen : zeilen.slice(0, 8);
  const zahl = (wert: number | null, stellen = 2) =>
    wert === null ? "—" : wert.toLocaleString(i18n.language, {
      minimumFractionDigits: stellen, maximumFractionDigits: stellen
    });

  return (
    <>
      <h2 className="mt-10 text-xl font-bold">{t("wasser.titel")}</h2>
      <p className="mt-1 text-sm text-leise">
        {verdacht.length > 0
          ? t("wasser.verdacht", { count: verdacht.length })
          : auffaellige.length > 0
            ? t("wasser.auffaellige", { count: auffaellige.length })
            : t("wasser.ruhig")}
      </p>

      <div className="mt-3 overflow-x-auto rounded-card bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-linie text-start text-xs uppercase tracking-wide text-leise">
              <th className="p-3 text-start">{t("wasser.objekt")}</th>
              <th className="p-3 text-start">{t("wasser.haus")}</th>
              <th className="p-3 text-end">{t("wasser.proTag")}</th>
              <th className="p-3 text-end">{t("wasser.ueblich")}</th>
              <th className="p-3 text-end">{t("wasser.grundlast")}</th>
              <th className="p-3 text-end">{t("wasser.woche")}</th>
              <th className="p-3 text-end">{t("wasser.monat")}</th>
              <th className="p-3 text-end">{t("wasser.vormonat")}</th>
              <th className="p-3 text-start">{t("wasser.abgelesen")}</th>
            </tr>
          </thead>
          <tbody>
            {gezeigt.map((z) => (
              <tr
                key={z.objektId}
                className={`border-b border-linie last:border-0 ${
                  z.leckverdacht ? "bg-red-50" : z.auffaellig ? "bg-marke/10" : ""
                }`}
              >
                <td className="p-3">
                  <Link to={`/kunden/${z.kundeId}`} className="font-semibold hover:underline">
                    {z.kunde}
                  </Link>
                  <span className="block text-xs text-leise">{z.objekt}</span>
                </td>
                {/*
                  Ob jemand da war, steht neben der Zahl und nicht im
                  Kleingedruckten: ohne diese Angabe ist der Verbrauch keine
                  Aussage, sondern nur eine Zahl.
                */}
                <td className="p-3 text-xs">
                  {z.leer === null ? (
                    <span className="text-leise">—</span>
                  ) : z.leer ? (
                    <span className="text-leise">{t("wasser.hausLeer")}</span>
                  ) : (
                    <span>{t("wasser.hausBelegt", { count: z.belegteTage ?? 0 })}</span>
                  )}
                </td>
                <td className={`p-3 text-end tabular-nums ${
                  z.leckverdacht ? "font-bold text-red-800" : z.auffaellig ? "font-semibold text-verweis" : ""
                }`}>
                  {zahl(z.proTag)}
                  {z.leckverdacht ? (
                    <span className="ms-2 rounded bg-red-700 px-1.5 py-0.5 text-[10px] font-bold text-white">
                      {t("wasser.marke")}
                    </span>
                  ) : z.auffaellig ? (
                    <span className="ms-2 rounded bg-marke px-1.5 py-0.5 text-[10px] font-bold text-white">
                      {t("wasser.markeMehr")}
                    </span>
                  ) : null}
                </td>
                <td className="p-3 text-end tabular-nums text-leise">
                  {zahl(z.basis)}
                  {/* Ohne Vergleichswert auch keine Herkunft: "alle Wochen"
                      unter einem Strich ist eine Auskunft ueber nichts. */}
                  {z.basis !== null && (
                    <span className="block text-[10px] uppercase tracking-wide">
                      {t(`wasser.vergleich.${z.vergleich}`)}
                    </span>
                  )}
                </td>
                <td className="p-3 text-end tabular-nums text-leise">{zahl(z.grundlast)}</td>
                <td className="p-3 text-end tabular-nums">{zahl(z.letzteWoche, 1)}</td>
                <td className="p-3 text-end tabular-nums">{zahl(z.monatProTag)}</td>
                <td className="p-3 text-end tabular-nums text-leise">{zahl(z.vormonatProTag)}</td>
                <td className="p-3 text-xs text-leise tabular-nums">
                  {new Date(z.letzteAblesung).toLocaleDateString(i18n.language)}
                  {z.tageImAbschnitt !== null && (
                    <span className="block">{t("wasser.tage", { count: z.tageImAbschnitt })}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-2 text-xs text-leise">{t("wasser.hinweis")}</p>
      {zeilen.length > gezeigt.length && (
        <button
          className="mt-2 text-sm font-semibold text-verweis underline"
          onClick={() => setAlle(true)}
        >
          {t("wasser.alleZeigen", { count: zeilen.length })}
        </button>
      )}
    </>
  );
}
